import { useState, useEffect } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Calendar, MessageSquare, Heart, Star, CreditCard, Settings, LogOut,
  Menu, X, ChevronRight, Users, Clock, Trash2, Upload, Camera, Shield, ArrowLeft,
  ArrowUpRight, ArrowDownRight, Search, Repeat, Receipt, FileText, DollarSign,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { ENLogo } from '@/components/ENLogo';
import DeleteAccountModal from '@/components/DeleteAccountModal';
import { MilestoneTimeline, CustomerConfirmButton, ReviewForm } from '@/components/MilestoneTracker';
import TipButton from '@/components/TipButton';
import TransactionsPage from '@/components/TransactionsPage';
import FeedbackForm from '@/components/feedback/FeedbackForm';
import { MobileBottomNav } from '@/components/MobileNav';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Loader2 } from 'lucide-react';
import ConsumerInquiries from '@/components/consumer/ConsumerInquiries';
import { useConsumerInquiries } from '@/hooks/useConsumerInquiries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sidebarLinks = [
  { path: '/dashboard/consumer', label: 'Overview', icon: LayoutDashboard },
  { path: '/dashboard/consumer/orders', label: 'Manage Orders', icon: FileText },
  { path: '/dashboard/consumer/transactions', label: 'Transactions', icon: Receipt },
  { path: '/dashboard/consumer/saved', label: 'Saved Vendors', icon: Heart },
  { path: '/dashboard/consumer/feedback', label: 'Feedback', icon: MessageSquare },
  { path: '/messages', label: 'Messages', icon: MessageSquare },
  { path: '/dashboard/consumer/settings', label: 'Settings', icon: Settings },
];

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.4)] rounded-2xl transition-all duration-300 ${className}`} {...props}>{children}</div>
);

function DashboardLayout({ children }) {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const isMobile = useIsMobile();
  const { pendingAction: pendingPayCount } = useConsumerInquiries();

  useEffect(() => {
    const fetchSaved = async () => {
      try {
        const res = await fetch(`${API}/saved-vendors`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setSavedCount(Array.isArray(data) ? data.length : 0);
        }
      } catch {
        /* ignore */
      }
    };
    fetchSaved();
    // Refresh badge when navigating back to any dashboard route
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const mobileTabs = [
    { path: '/dashboard/consumer', label: 'Home', icon: LayoutDashboard, id: 'home', exact: true },
    { path: '/dashboard/consumer/orders', label: 'Orders', icon: FileText, id: 'orders', badge: pendingPayCount, badgePulse: pendingPayCount > 0 },
    { path: '/messages', label: 'Messages', icon: MessageSquare, id: 'messages' },
    { path: '/dashboard/consumer/saved', label: 'Saved', icon: Heart, id: 'saved', badge: savedCount },
    { path: '/dashboard/consumer/settings', label: 'Profile', icon: Settings, id: 'profile' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] text-white font-sans" data-testid="consumer-dashboard">
      {/* Background grid pattern */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      {/* Sidebar — desktop only */}
      <aside className={`fixed left-0 top-0 h-screen w-[260px] bg-[#1E1E3A]/80 backdrop-blur-2xl border-r border-white/5 flex-col z-50 transition-transform duration-300 hidden lg:flex`} data-testid="consumer-sidebar">
        {/* Logo */}
        <div className="px-6 pt-7 pb-6">
          <ENLogo size="md" linkTo="" />
        </div>

        {/* Back to Marketplace */}
        <div className="px-4 mb-4">
          <Link to="" className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all text-sm">
            <ArrowLeft size={16} /> Back to Marketplace
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          {sidebarLinks.map((link) => {
            const active = isActive(link.path);
            return (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all duration-200 group relative ${
                  active
                    ? 'text-white bg-gradient-to-r from-[#8B5CF6]/30 to-[#3B82F6]/20 shadow-[0_0_20px_rgba(139,92,246,0.2)]'
                    : 'text-[#9CA3AF] hover:text-white hover:bg-white/5'
                }`}
                data-testid={`consumer-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-gradient-to-b from-[#8B5CF6] to-[#3B82F6] rounded-r-full" />}
                <link.icon size={18} strokeWidth={active ? 2 : 1.5} className={active ? 'text-[#A78BFA]' : 'group-hover:text-white'} />
                <span className="flex-1">{link.label}</span>
                {link.label === 'Manage Orders' && pendingPayCount > 0 && (
                  <span
                    className="ml-auto min-w-[22px] h-5 px-1.5 rounded-full bg-yellow-400 text-[#0E0E23] text-[11px] font-bold flex items-center justify-center shadow-[0_0_12px_rgba(252,211,77,0.65)] border border-yellow-200"
                    style={{ animation: 'inquiry-badge-pulse 1.4s ease-in-out infinite' }}
                    data-testid="consumer-sidebar-inquiry-badge"
                  >
                    {pendingPayCount > 99 ? '99+' : pendingPayCount}
                  </span>
                )}
              </Link>
            );
          })}
          <style>{`
            @keyframes inquiry-badge-pulse {
              0%, 100% { transform: scale(1); box-shadow: 0 0 12px rgba(252,211,77,0.65); }
              50%      { transform: scale(1.12); box-shadow: 0 0 22px 4px rgba(252,211,77,0.85); }
            }
          `}</style>
        </nav>

        {/* Become a Vendor CTA */}
        {!user?.roles?.includes('vendor') && (
          <div className="px-4 pb-2">
            <Link to="/onboarding/vendor">
              <button className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] transition-all" data-testid="become-vendor-sidebar">
                Become a Vendor
              </button>
            </Link>
          </div>
        )}

        {/* User / Logout */}
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center text-sm font-bold shadow-[0_0_10px_rgba(139,92,246,0.3)]">
              {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'C'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium truncate">{user?.name || user?.email?.split('@')[0]}</p>
              <p className="text-[11px] text-[#9CA3AF]">Consumer</p>
            </div>
            <button onClick={handleLogout} className="text-[#9CA3AF] hover:text-[#FF6B6B] transition-colors" data-testid="consumer-sidebar-logout">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-[260px] min-h-screen relative">
        <div className="p-4 pb-24 lg:p-10 lg:pb-10">{children}</div>
      </main>

      {/* Mobile Bottom Nav */}
      {isMobile && <MobileBottomNav tabs={mobileTabs} />}
    </div>
  );
}

function Overview() {
  const { getAuthHeaders, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { total: totalInquiries, openCount, pendingAction: pendingPayCount } = useConsumerInquiries();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        fetch(`${API}/stats/consumer`, { headers: getAuthHeaders() }),
        fetch(`${API}/bookings/my-bookings?role=consumer`, { headers: getAuthHeaders() })
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (bookingsRes.ok) setBookings((await bookingsRes.json()).slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne" data-testid="consumer-dashboard-title">Consumer Dashboard</h1>
          <p className="text-sm text-[#9CA3AF] mt-1">Discover and support Indigenous businesses</p>
        </div>
        <div className="hidden sm:flex items-center gap-3">
          <div className="px-4 py-2.5 bg-[#2D2D52]/40 border border-white/10 rounded-xl text-sm text-[#9CA3AF]">
            {greeting()}, <span className="text-white font-medium">{user?.name || user?.email?.split('@')[0]}</span>
          </div>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { label: 'Manage Orders', value: openCount, sublabel: `${stats?.total_bookings || 0} total`, icon: FileText, to: '/dashboard/consumer/orders' },
          { label: 'Active Now', value: stats?.active_bookings || 0, icon: Clock, to: '/dashboard/consumer/orders' },
          { label: 'Saved Vendors', value: stats?.saved_vendors || 0, icon: Heart, change: 3.2, positive: true, to: '/dashboard/consumer/saved' },
          { label: 'Unread Messages', value: stats?.unread_messages || 0, icon: MessageSquare, to: '/messages' },
        ].map((stat, i) => {
          const inner = (
            <>
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-6 translate-x-6" />
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-4 -translate-x-4" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2.5 rounded-xl bg-white/20 backdrop-blur-sm"><stat.icon size={20} className="text-white" strokeWidth={2} /></div>
                </div>
                <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-white/70 font-medium">{stat.label}</p>
                  {stat.sublabel && <span className="text-xs text-white/60 font-semibold">{stat.sublabel}</span>}
                  {stat.change && <span className="flex items-center gap-0.5 text-xs font-bold text-[#86EFAC]"><ArrowUpRight size={14} />{stat.change}%</span>}
                </div>
              </div>
            </>
          );
          const className = "relative bg-gradient-to-br from-[#8B5CF6] via-[#6D28D9] to-[#3B82F6] rounded-2xl p-6 overflow-hidden group hover:shadow-[0_0_30px_rgba(139,92,246,0.4)] transition-all duration-300 block";
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              data-testid={`consumer-metric-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              {stat.to
                ? <Link to={stat.to} className={className}>{inner}</Link>
                : <div className={className}>{inner}</div>}
            </motion.div>
          );
        })}
      </div>

      {/* Manage Orders summary — gold pulse when a vendor has accepted and is awaiting payment */}
      <Link
        to="/dashboard/consumer/orders"
        className={`block relative rounded-2xl border p-5 transition-all overflow-hidden group ${
          pendingPayCount > 0
            ? 'border-yellow-300/70 bg-gradient-to-r from-yellow-400/15 via-amber-400/10 to-yellow-300/15'
            : 'border-white/10 bg-white/[0.03] hover:border-fuchsia-400/30'
        }`}
        style={pendingPayCount > 0 ? { animation: 'inquiries-tile-pulse 1.6s ease-in-out infinite' } : undefined}
        data-testid="consumer-manage-orders-tile"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
              pendingPayCount > 0
                ? 'bg-yellow-400 text-[#0E0E23] shadow-[0_0_22px_rgba(252,211,77,0.7)]'
                : 'bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-400/20'
            }`}>
              {pendingPayCount > 0 ? <DollarSign size={22} strokeWidth={2.5} /> : <FileText size={20} />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white font-bold text-base">
                {pendingPayCount > 0
                  ? `${pendingPayCount} offer${pendingPayCount === 1 ? '' : 's'} awaiting payment`
                  : 'Manage Orders'}
              </p>
              <p className={`text-sm mt-0.5 ${pendingPayCount > 0 ? 'text-yellow-100/90' : 'text-zinc-400'}`}>
                {pendingPayCount > 0
                  ? 'A vendor accepted your request — pay to start the work, or reject if you changed your mind.'
                  : `${openCount} open · ${totalInquiries} total · view every inquiry, active job & past purchase`}
              </p>
            </div>
          </div>
          <ChevronRight size={22} className={pendingPayCount > 0 ? 'text-yellow-100' : 'text-zinc-500 group-hover:text-white'} />
        </div>
        <style>{`
          @keyframes inquiries-tile-pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(252, 211, 77, 0.3); }
            50%      { box-shadow: 0 0 32px 4px rgba(252, 211, 77, 0.55); }
          }
        `}</style>
      </Link>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Link to="/browse">
          <GlassCard className="p-6 hover:border-[#8B5CF6]/30 cursor-pointer group">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-[#8B5CF6]/20 to-[#3B82F6]/20 group-hover:from-[#8B5CF6]/30 group-hover:to-[#3B82F6]/30 transition-all">
                <Search size={20} className="text-[#A78BFA]" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Find a Vendor</p>
                <p className="text-[#9CA3AF] text-xs">Browse Indigenous businesses</p>
              </div>
            </div>
          </GlassCard>
        </Link>
        <Link to="/dashboard/consumer/saved">
          <GlassCard className="p-6 hover:border-[#8B5CF6]/30 cursor-pointer group">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-[#EC4899]/20 to-[#8B5CF6]/20 group-hover:from-[#EC4899]/30 group-hover:to-[#8B5CF6]/30 transition-all">
                <Heart size={20} className="text-[#EC4899]" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Saved Vendors</p>
                <p className="text-[#9CA3AF] text-xs">View your favorites</p>
              </div>
            </div>
          </GlassCard>
        </Link>
      </div>
    </div>
  );
}

function Bookings() {
  const { getAuthHeaders } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [milestoneData, setMilestoneData] = useState({});
  const [reviewed, setReviewed] = useState({});

  useEffect(() => { fetchBookings(); }, []);

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API}/bookings/my-bookings?role=consumer`, { headers: getAuthHeaders() });
      if (response.ok) setBookings(await response.json());
    } catch {} finally { setLoading(false); }
  };

  const fetchMilestones = async (bookingId) => {
    try {
      const response = await fetch(`${API}/milestones/booking/${bookingId}/`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setMilestoneData(prev => ({ ...prev, [bookingId]: data }));
      }
    } catch {}
  };

  const toggleBooking = (bookingId) => {
    if (expandedBooking === bookingId) { setExpandedBooking(null); return; }
    setExpandedBooking(bookingId);
    if (!milestoneData[bookingId]) fetchMilestones(bookingId);
  };

  const statusColors = { pending: 'bg-yellow-500/15 text-[#FCD34D] border-yellow-500/20', in_progress: 'bg-blue-500/15 text-[#93C5FD] border-blue-500/20', shipped: 'bg-purple-500/15 text-[#C4B5FD] border-purple-500/20', delivered: 'bg-purple-500/15 text-[#C4B5FD] border-purple-500/20', completed: 'bg-green-500/15 text-[#86EFAC] border-green-500/20', booked: 'bg-yellow-500/15 text-[#FCD34D] border-yellow-500/20' };
  const needsConfirm = (b) => ['shipped', 'delivered', 'completed'].includes(b.status) && b.escrow_status !== 'released' && !b.customer_confirmed_at;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne" data-testid="bookings-title">My Bookings</h1>
        <p className="text-sm text-[#9CA3AF] mt-1">Track milestones, confirm deliveries, and leave reviews</p>
      </div>
      {loading ? <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-white/5 rounded-2xl animate-pulse" />)}</div> : bookings.length > 0 ? (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <GlassCard key={booking.booking_id} className="overflow-hidden" data-testid={`booking-${booking.booking_id}`}>
              <button onClick={() => toggleBooking(booking.booking_id)} className="w-full p-5 flex items-center justify-between hover:bg-white/5 transition-all text-left">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6]/40 to-[#3B82F6]/40 flex items-center justify-center"><Calendar size={18} className="text-white" /></div>
                  <div><p className="text-white font-semibold text-sm">{booking.service_title || 'Service'}</p><p className="text-[#64748B] text-xs">{booking.vendor_name || 'Vendor'} · {booking.created_at ? new Date(booking.created_at).toLocaleDateString() : ''}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#A78BFA] font-bold">${booking.price}</span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusColors[booking.status] || statusColors.pending}`}>{(booking.status || 'pending').replace(/_/g, ' ')}</span>
                  <ChevronRight size={16} className={`text-[#9CA3AF] transition-transform ${expandedBooking === booking.booking_id ? 'rotate-90' : ''}`} />
                </div>
              </button>
              {expandedBooking === booking.booking_id && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="border-t border-white/5 p-5 space-y-5">
                  <div className="flex items-center gap-3"><Shield size={14} className="text-[#64748B]" /><div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] rounded-full" style={{ width: booking.escrow_status === 'released' ? '100%' : booking.escrow_status === 'down_payment_received' ? '50%' : '10%' }} /></div><span className="text-[#64748B] text-xs">{booking.escrow_status === 'released' ? 'Released' : booking.escrow_status === 'down_payment_received' ? '50% Held' : 'Pending'}</span></div>
                  {milestoneData[booking.booking_id] ? (
                    <MilestoneTimeline
                      milestones={milestoneData[booking.booking_id].milestones}
                      currentStep={milestoneData[booking.booking_id].current_step}
                      isVendor={false}
                      bookingId={booking.booking_id}
                      trackingNumber={milestoneData[booking.booking_id].tracking_number}
                      progressPercent={milestoneData[booking.booking_id].progress_percent}
                      progressLabel={milestoneData[booking.booking_id].progress_label}
                      revisionsUsed={milestoneData[booking.booking_id].revisions_used}
                      revisionsLimit={milestoneData[booking.booking_id].revisions_limit}
                      revisionsRemaining={milestoneData[booking.booking_id].revisions_remaining}
                      onUpdate={() => { fetchMilestones(booking.booking_id); fetchBookings(); }}
                    />
                  ) : <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-[#8B5CF6]" /></div>}
                  {needsConfirm(booking) && <CustomerConfirmButton bookingId={booking.booking_id} vendorType={booking.vendor_type || 'product'} onConfirm={() => { fetchBookings(); fetchMilestones(booking.booking_id); }} />}
                  {(booking.status === 'completed' || booking.status === 'in_progress' || booking.status === 'paid') && (
                    <TipButton booking={booking} vendorName={booking.vendor_name} onTipSent={fetchBookings} />
                  )}
                  {booking.status === 'completed' && booking.escrow_status === 'released' && !reviewed[booking.booking_id] && <ReviewForm bookingId={booking.booking_id} onSubmit={() => setReviewed(prev => ({ ...prev, [booking.booking_id]: true }))} />}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Link to={`/messages/${booking.vendor_id}`}><Button variant="outline" className="border-white/10 text-[#9CA3AF] hover:text-white text-xs"><MessageSquare size={12} className="mr-1.5" /> Message Vendor</Button></Link>
                    {booking.status === 'completed' && booking.service_id && (
                      <Link
                        to={`/booking/${booking.service_id}?reorder=${booking.booking_id}`}
                        data-testid={`reorder-${booking.booking_id}`}
                      >
                        <Button className="bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-xs">
                          <Repeat size={12} className="mr-1.5" /> Order this again
                        </Button>
                      </Link>
                    )}
                  </div>
                </motion.div>
              )}
            </GlassCard>
          ))}
        </div>
      ) : (
        <GlassCard className="text-center py-12 px-6">
          <Calendar size={40} className="mx-auto mb-4 text-[#9CA3AF] opacity-40" /><p className="text-[#9CA3AF] mb-4">No bookings yet</p>
          <Link to="/browse"><button className="px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-sm">Browse Vendors</button></Link>
        </GlassCard>
      )}
    </div>
  );
}

function SavedVendors() {
  const { getAuthHeaders } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchSaved(); }, []);

  const fetchSaved = async () => {
    try {
      const response = await fetch(`${API}/saved-vendors`, { headers: getAuthHeaders() });
      if (response.ok) setVendors(await response.json());
    } catch (error) { console.error('Failed to fetch saved vendors:', error); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne" data-testid="saved-vendors-title">Saved Vendors</h1>
        <p className="text-sm text-[#9CA3AF] mt-1">Your favorite Indigenous businesses</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-white/5 rounded-2xl animate-pulse" />)}</div>
      ) : vendors.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {vendors.map((vendor) => (
            <Link key={vendor.vendor_id} to={`/vendor/${vendor.vendor_id}`} data-testid={`saved-vendor-${vendor.vendor_id}`}>
              <GlassCard className="p-5 hover:border-[#8B5CF6]/30 cursor-pointer group">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/5 flex-shrink-0">
                    {vendor.profile_image ? (
                      <img src={vendor.profile_image} alt={vendor.business_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Users size={24} className="text-[#64748B]" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold truncate">{vendor.business_name}</h3>
                    <p className="text-[#9CA3AF] text-sm">{vendor.location}</p>
                    {vendor.rating && (
                      <div className="flex items-center gap-1 mt-1"><Star size={12} className="text-[#F59E0B] fill-[#F59E0B]" /><span className="text-[#9CA3AF] text-xs">{vendor.rating?.toFixed(1)}</span></div>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-[#64748B] group-hover:text-[#A78BFA] transition-colors" />
                </div>
              </GlassCard>
            </Link>
          ))}
        </div>
      ) : (
        <GlassCard className="text-center py-12 px-6">
          <Heart size={40} className="mx-auto mb-4 text-[#9CA3AF] opacity-40" />
          <p className="text-[#9CA3AF] mb-4">No saved vendors</p>
          <Link to="/browse">
            <button className="px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] transition-all text-sm">Browse Vendors</button>
          </Link>
        </GlassCard>
      )}
    </div>
  );
}

function AccountSettings() {
  const navigate = useNavigate();
  const { user, getAuthHeaders, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [tribalAffiliation, setTribalAffiliation] = useState('');

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/users/profile`, { headers: getAuthHeaders() });
      if (response.ok) { const data = await response.json(); setProfile(data); setName(data.name || ''); setPhone(data.phone || ''); setLocation(data.location || ''); setTribalAffiliation(data.tribal_affiliation || ''); }
    } catch (error) { console.error('Failed to fetch profile:', error); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/users/profile`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ name, phone, location, tribal_affiliation: tribalAffiliation }) });
      if (response.ok) toast.success('Profile updated!');
      else throw new Error('Failed');
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API}/users/upload-media?media_type=profile_picture`, { method: 'POST', headers: getAuthHeaders(), body: formData });
      if (response.ok) { const data = await response.json(); setProfile(prev => ({ ...prev, picture: data.url })); toast.success('Profile picture updated!'); }
      else throw new Error('Upload failed');
    } catch { toast.error('Failed to upload image'); }
    finally { setUploading(false); }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${API}/users/delete-account`, { method: 'DELETE', headers: getAuthHeaders() });
      if (response.ok) { toast.success('Account deleted'); await logout(); navigate('/'); }
      else throw new Error('Failed');
    } catch { toast.error('Failed to delete account'); }
    finally { setDeleting(false); }
  };

  if (loading) return <div className="space-y-4"><div className="h-12 bg-white/5 rounded-2xl animate-pulse" /><div className="h-48 bg-white/5 rounded-2xl animate-pulse" /></div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne" data-testid="settings-title">Account Settings</h1>
        <p className="text-sm text-[#9CA3AF] mt-1">Manage your profile and preferences</p>
      </div>

      {/* Profile Picture */}
      <GlassCard className="p-6">
        <h2 className="text-lg font-semibold text-white font-syne mb-4">Profile Picture</h2>
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 rounded-2xl overflow-hidden bg-white/5 flex-shrink-0">
            {profile?.picture ? (
              <img src={profile.picture.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${profile.picture}` : profile.picture} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#9CA3AF]"><Camera size={32} /></div>
            )}
          </div>
          <div>
            <input type="file" accept="image/*" onChange={handleProfilePictureUpload} className="hidden" id="consumer-profile-picture-upload" data-testid="consumer-profile-picture-upload" />
            <label htmlFor="consumer-profile-picture-upload">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] cursor-pointer hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all text-sm">
                <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload Picture'}
              </span>
            </label>
            <p className="text-[#9CA3AF] text-xs mt-2">JPG, PNG, or GIF. Max 10MB.</p>
          </div>
        </div>
      </GlassCard>

      {/* Personal Info */}
      <GlassCard className="p-6 space-y-5">
        <h2 className="text-lg font-semibold text-white font-syne">Personal Information</h2>
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-1 block">Tribal Affiliation or Ethnicity</Label>
          <Input value={tribalAffiliation} onChange={(e) => setTribalAffiliation(e.target.value)} placeholder="e.g., Navajo Nation, Cherokee Nation" className="bg-white/5 border-white/10 text-white rounded-xl h-11 placeholder:text-white/20" data-testid="edit-tribal-affiliation" />
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-1 block">Full Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10 text-white rounded-xl h-11" data-testid="edit-name" />
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-1 block">Phone Number</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (555) 123-4567" className="bg-white/5 border-white/10 text-white rounded-xl h-11 placeholder:text-white/20" data-testid="edit-phone" />
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-1 block">Location</Label>
          <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, State" className="bg-white/5 border-white/10 text-white rounded-xl h-11 placeholder:text-white/20" data-testid="edit-location" />
        </div>
        <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all text-sm" data-testid="save-consumer-profile">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </GlassCard>

      {/* Sign Out */}
      <GlassCard className="p-6">
        <h2 className="text-lg font-semibold text-white font-syne mb-3">Account</h2>
        <button
          onClick={async () => { await logout(); navigate('/'); }}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium text-[#9CA3AF] border border-white/10 hover:text-white hover:bg-white/5 transition-all"
          data-testid="consumer-sign-out-btn"
        >
          <LogOut size={16} /> Sign Out
        </button>
      </GlassCard>

      {/* Danger Zone */}
      <GlassCard className="p-6 border-red-500/20">
        <h2 className="text-lg font-semibold text-[#FCA5A5] font-syne mb-4">Danger Zone</h2>
        <p className="text-[#9CA3AF] text-sm mb-4">Permanently delete your account. We'll ask 3 quick questions before proceeding.</p>
        <button onClick={() => setShowDeleteConfirm(true)} className="px-4 py-2 rounded-xl text-sm font-medium text-[#FCA5A5] border border-red-500/30 hover:bg-red-500/10 transition-all" data-testid="consumer-delete-account-btn">
          <Trash2 size={14} className="inline mr-2" /> Delete Account
        </button>
      </GlassCard>
      <DeleteAccountModal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} />
    </div>
  );
}

export default function ConsumerDashboard() {
  return (
    <DashboardLayout>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="orders" element={<ConsumerInquiries />} />
        {/* Back-compat: redirect old /inquiries + /bookings to the unified Manage Orders page */}
        <Route path="inquiries" element={<ConsumerInquiries />} />
        <Route path="bookings" element={<ConsumerInquiries />} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="saved" element={<SavedVendors />} />
        <Route path="feedback" element={<FeedbackForm source="ongoing" />} />
        <Route path="settings" element={<AccountSettings />} />
      </Routes>
    </DashboardLayout>
  );
}
